import { EventGroupTicketGroupItem, TicketGroup } from 'src/WebApiController';

import { EventGroupTicketGroupDraft } from './EventGroup.types';

/**
 * Converts ticket group drafts into API-ready EventGroupTicketGroupItem[][].
 *
 * Returns one array per event (outer index = event slot). Each group's total
 * cost (draft.unitCost × draft.quantity — the all-events total the user
 * entered) is distributed independently across `eventCount` events via
 * distributeGroupCostAcrossEvents, so different events may receive unitCost
 * values that differ by at most $0.01.
 *
 * This guarantees that, for each group, the sum of unitCost × qty across all
 * events equals the original group cost entered by the user — exactly, with
 * zero rounding error.
 */
export function buildTicketGroupItems(
  ticketGroups: EventGroupTicketGroupDraft[],
  eventCount: number,
  totalCost: number | null
): EventGroupTicketGroupItem[][] {
  if (ticketGroups.length === 0 || eventCount < 1 || totalCost == null)
    return [];

  const filtered = ticketGroups.filter((tg) => tg.quantity > 0);
  if (filtered.length === 0) return [];

  // For each group, compute per-event unitCost values.
  // perEventUnitCosts[groupIndex][eventIndex] = unitCost for that event.
  const perEventUnitCosts = filtered.map((tg) => {
    const groupTotalCostCents = Math.round(tg.unitCost * tg.quantity * 100);
    return distributeGroupCostAcrossEvents(
      groupTotalCostCents,
      tg.quantity,
      eventCount
    );
  });

  // Build the result: one array of ticket group items per event.
  const result: EventGroupTicketGroupItem[][] = [];
  for (let eventIdx = 0; eventIdx < eventCount; eventIdx++) {
    result.push(
      filtered.map((tg, groupIdx) => ({
        section: tg.section,
        row: tg.row || null,
        quantity: tg.quantity,
        unitCost: perEventUnitCosts[groupIdx][eventIdx],
        seatFrom: tg.seatFrom || null,
        seatTo: tg.seatTo || null,
        deliveryType: tg.deliveryType,
        currencyCode: tg.currencyCode,
      }))
    );
  }

  return result;
}

/**
 * Distributes a single ticket group's total cost (in cents) across N events.
 *
 * Each event receives a whole-cent unitCost such that:
 *   sum(unitCost[i] × qty, for i in 0..eventCount-1) === groupTotalCostCents
 *
 * Algorithm:
 *   1. Compute the base per-ticket-per-event cost:
 *      baseUnitCostCents = floor(groupTotalCostCents / (qty × eventCount))
 *   2. The remainder cents that cannot be evenly distributed:
 *      remainderCents = groupTotalCostCents - baseUnitCostCents × qty × eventCount
 *   3. Each +$0.01 bump to a single event's unitCost adds `qty` cents to the
 *      grand total. So we need floor(remainderCents / qty) events at +$0.01.
 *   4. If remainderCents is not divisible by qty, the leftover
 *      (remainderCents % qty) cents cannot be placed without fractional-cent
 *      unitCost — this means the input groupTotalCostCents was not divisible
 *      by qty, which shouldn't happen when the form computes
 *      totalCost = unitCost × qty. If it does, the loss is at most (qty − 1)
 *      cents.
 *
 * @param groupTotalCostCents - the group's total cost across all events, in cents
 * @param qty                 - number of tickets in this group (per event)
 * @param eventCount          - number of events (slots)
 * @returns array of unitCost values (in dollars), one per event
 */
export function distributeGroupCostAcrossEvents(
  groupTotalCostCents: number,
  qty: number,
  eventCount: number
): number[] {
  if (eventCount <= 0 || qty <= 0) return new Array(eventCount).fill(0);

  const totalSlots = qty * eventCount;
  const baseUnitCostCents = Math.floor(groupTotalCostCents / totalSlots);
  const remainderCents = groupTotalCostCents - baseUnitCostCents * totalSlots;

  // Each +$0.01 bump on one event's unitCost costs qty cents from the remainder.
  const bumpedEventCount = Math.floor(remainderCents / qty);

  return Array.from({ length: eventCount }, (_, i) => {
    const unitCostCents = baseUnitCostCents + (i < bumpedEventCount ? 1 : 0);
    return unitCostCents / 100;
  });
}

/**
 * Builds EventGroupTicketGroupItem[] by copying from existing placeholder
 * event's ticket groups, recalculating unitCost as the average cost per ticket.
 */
export function buildTicketGroupItemsFromExisting(
  existingTicketGroups: TicketGroup[],
  totalGroupCost: number,
  totalTicketCount: number
): EventGroupTicketGroupItem[] {
  if (existingTicketGroups.length === 0 || totalTicketCount <= 0) return [];

  const unitCost = Math.floor((totalGroupCost / totalTicketCount) * 100) / 100;

  return existingTicketGroups.map((tg) => ({
    section: tg.seating?.section ?? '',
    row: tg.seating?.row ?? null,
    quantity: tg.ticketCnt ?? 0,
    unitCost,
    seatFrom: tg.seating?.seatFr ?? null,
    seatTo: tg.seating?.seatTo ?? null,
    deliveryType: tg.delivType,
    currencyCode: tg.currency ?? '',
  }));
}
